import type { OnChainVenue } from 'utils/swap/types'

/**
 * The arithmetic behind the stock board, kept apart from the chain reads.
 *
 * These are the functions that decide what a visitor is told a share costs and
 * whether the pool is trading above or below the issuer's own oracle. A sign
 * the wrong way round here is not a cosmetic bug — it is a page telling someone
 * they are buying at a discount while they are paying a premium. Pure, so it
 * can be proven without a node.
 */

/** One pool of one stock, as the board and the venue table see it. */
export interface StockPool {
  venue: OnChainVenue
  address: string
  stable?: boolean
  fee?: number
  tickSpacing?: number
  /** USDC held by the pool, in USDC units (6 decimals). */
  reserveQuote: bigint
  /** The stock token held by the pool, in its own units (8 decimals). */
  reserveBase: bigint
  /** What the pool returned for `probeAmountIn` USDC, or null when it refused. */
  quotedOut: bigint | null
}

/** The USDC the board offers each pool, to find a price without moving it. */
export const PROBE_USDC = 100n * 10n ** 6n

/**
 * A price in dollars from a quote, or null when the pool could not fill it.
 *
 * Floating point enters here and nowhere earlier: a price is displayed, never
 * used to size a transaction. What sizes a transaction is the swap page's own
 * integer path, which does not import this file.
 */
export function priceFromQuote(
  amountInUsdc: bigint,
  amountOutBase: bigint | null,
  baseDecimals: number,
  quoteDecimals = 6
): number | null {
  if (amountOutBase === null || amountOutBase <= 0n || amountInUsdc <= 0n) return null
  const spent = Number(amountInUsdc) / 10 ** quoteDecimals
  const received = Number(amountOutBase) / 10 ** baseDecimals
  if (!Number.isFinite(spent) || !Number.isFinite(received) || received === 0) return null
  return spent / received
}

/** A Chainlink answer in dollars. The feeds publish 8 decimals. */
export function priceFromFeed(answer: bigint | null, decimals = 8): number | null {
  if (answer === null || answer <= 0n) return null
  return Number(answer) / 10 ** decimals
}

/**
 * How far the pool sits from the oracle, in basis points, **positive when the
 * pool is dearer**. Both numbers describe the same share, so the difference is
 * the only honest measure of whether this venue is worth trading on right now.
 */
export function premiumBps(poolPrice: number | null, oraclePrice: number | null): number | null {
  if (poolPrice === null || oraclePrice === null || oraclePrice <= 0) return null
  return Math.round(((poolPrice - oraclePrice) / oraclePrice) * 10000)
}

/**
 * What a pool is worth, in dollars, using the oracle for the stock side.
 *
 * The stock side is priced from the oracle rather than from the pool itself on
 * purpose: a pool holding nine hundred dollars of USDC against a rounding error
 * of a share would otherwise report its own broken price as depth, and depth is
 * exactly the number used to decide that pool should not be shown first.
 */
export function poolLiquidityUsd(pool: StockPool, oraclePrice: number | null, baseDecimals: number): number {
  const quote = Number(pool.reserveQuote) / 10 ** 6
  if (oraclePrice === null) return quote
  const base = (Number(pool.reserveBase) / 10 ** baseDecimals) * oraclePrice
  return quote + base
}

/**
 * The pool the board quotes: the deepest one that actually answered.
 *
 * Deepest rather than best-priced, and the distinction is the whole point. A
 * near-empty pool prints whatever price its last trade left behind — AAPLc on
 * Uniswap v3's 1 % tier held 952 USDC on 2026-09-11 and would happily quote a
 * number — so ranking by price would put the least trustworthy venue at the top
 * of the page. The swap ranks by what you receive, because there the trade is
 * priced for its real size; a board shows a market, so it shows the market.
 */
export function pickQuotingPool(
  pools: StockPool[],
  oraclePrice: number | null,
  baseDecimals: number
): StockPool | null {
  const answered = pools.filter((p) => p.quotedOut !== null && p.quotedOut > 0n)
  if (answered.length === 0) return null
  return answered.reduce((best, pool) =>
    poolLiquidityUsd(pool, oraclePrice, baseDecimals) > poolLiquidityUsd(best, oraclePrice, baseDecimals)
      ? pool
      : best
  )
}

/** Seconds since a Chainlink round was published, or null when unknown. */
export function feedAgeSeconds(updatedAt: number | null, nowMs: number = Date.now()): number | null {
  if (!updatedAt) return null
  return Math.max(0, Math.floor(nowMs / 1000) - updatedAt)
}

/**
 * A feed holds the last close at weekends and freezes during corporate
 * actions, so "old" is normal and must not be drawn as a fault. Beyond the
 * feeds' own 24-hour heartbeat, though, the page says the reading is stale —
 * that one is the issuer's own contract, not the market being shut.
 */
export const FEED_HEARTBEAT_SECONDS = 24 * 60 * 60

export function isFeedStale(updatedAt: number | null, nowMs: number = Date.now()): boolean {
  const age = feedAgeSeconds(updatedAt, nowMs)
  return age !== null && age > FEED_HEARTBEAT_SECONDS
}

/**
 * `multiplier()` as a human number. One token is one share while it reads
 * 1.0; a dividend or a split moves it, and a page that assumed 1.0 would then
 * quietly misprice every row.
 */
export function multiplierToNumber(multiplier: bigint | null): number | null {
  if (multiplier === null || multiplier <= 0n) return null
  return Number(multiplier) / 1e18
}
